import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBullseye, faSpinner, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import axios from '../../config/axiosConfig';
import { toast } from 'react-toastify';

const DEFAULT_BEACON_URL = 'https://www.netsolution.ch/logger/beacon.php';
const DEFAULT_SVG_BEACON_URL = 'https://www.netsolution.ch/logger/beacon-xss.svg';

const BeaconInjectionDialog = ({ exchangeId, onClose }) => {
  const { darkMode } = useTheme();
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [beaconUrl, setBeaconUrl] = useState(DEFAULT_BEACON_URL);
  const [svgBeaconUrl, setSvgBeaconUrl] = useState(DEFAULT_SVG_BEACON_URL);
  const [includeEventHandlers, setIncludeEventHandlers] = useState(true);
  const [injectSvgUpload, setInjectSvgUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    axios.post('/web-findings/beacon-injection/parse', { exchangeId })
      .then(({ data }) => {
        setFields(data.fields || []);
        setMeta({ method: data.method, url: data.url, isMultipart: data.isMultipart, hasFileField: data.hasFileField });
        const nonFile = (data.fields || []).filter(f => f.type !== 'file').map(f => f.name);
        setSelected(new Set(nonFile));
        if (data.hasFileField) setInjectSvgUpload(true);
        setLoading(false);
      })
      .catch(err => {
        toast.error('Failed to parse request: ' + (err.response?.data?.error || err.message));
        setLoading(false);
      });
  }, [exchangeId]);

  const toggleField = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(fields.filter(f => f.type !== 'file').map(f => f.name)));
  const selectNone = () => setSelected(new Set());

  const handleRun = async () => {
    if (!selected.size && !injectSvgUpload) {
      toast.warn('Select at least one field or enable SVG upload injection.');
      return;
    }
    setRunning(true);
    try {
      const { data } = await axios.post('/web-findings/beacon-injection', {
        exchangeId,
        beaconUrl,
        svgBeaconUrl,
        selectedFields: [...selected],
        includeEventHandlers,
        injectSvgUpload
      });
      setResults(data);
      if (data.findings?.length) {
        toast.warning(`${data.findings.length} beacon reflection(s) found! Check beacon logs for OOB hits.`);
      } else {
        toast.info(data.note || `${data.summary?.totalSent || 0} payloads sent. Monitor beacon logs for callbacks.`);
      }
    } catch (err) {
      toast.error('Beacon injection failed: ' + (err.response?.data?.error || err.message));
    }
    setRunning(false);
  };

  const bg = darkMode ? '#1f2937' : '#fff';
  const border = darkMode ? '#374151' : '#e5e7eb';
  const text = darkMode ? '#f3f4f6' : '#111827';
  const textMuted = darkMode ? '#9ca3af' : '#6b7280';
  const inputBg = darkMode ? '#374151' : '#fff';
  const inputBorder = darkMode ? '#4b5563' : '#d1d5db';

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: bg, borderRadius: 12, padding: 28, maxWidth: 700, width: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: `1px solid ${border}`, color: text }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 14, borderBottom: `2px solid ${border}` }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            <FontAwesomeIcon icon={faBullseye} style={{ color: '#ef4444', marginRight: 8 }} />
            Beacon Injection Scanner
          </h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: textMuted, cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Target info */}
        {meta.url && (
          <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, background: darkMode ? '#111827' : '#f9fafb', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
            <span style={{ color: meta.method === 'POST' ? '#f59e0b' : '#10b981', fontWeight: 700, marginRight: 8 }}>{meta.method}</span>
            {meta.url}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: textMuted }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} /> Parsing request parameters...
          </div>
        ) : !results ? (
          <>
            {/* Field selection */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Injection Points ({fields.filter(f => f.type !== 'file').length} fields)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={selectAll} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: `1px solid ${inputBorder}`, background: inputBg, color: text, cursor: 'pointer' }}>All</button>
                  <button onClick={selectNone} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: `1px solid ${inputBorder}`, background: inputBg, color: text, cursor: 'pointer' }}>None</button>
                </div>
              </div>
              {fields.length === 0 && (
                <div style={{ color: textMuted, fontSize: 13, fontStyle: 'italic' }}>No injectable fields found in this request.</div>
              )}
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fields.map((f, i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: f.type === 'file' ? 'default' : 'pointer', opacity: f.type === 'file' ? 0.5 : 1 }}>
                    {f.type !== 'file' && (
                      <input type="checkbox" checked={selected.has(f.name)} onChange={() => toggleField(f.name)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    )}
                    {f.type === 'file' && <span style={{ width: 16, textAlign: 'center' }}>📎</span>}
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: darkMode ? '#60a5fa' : '#2563eb', minWidth: 100 }}>{f.name}</span>
                    <span style={{ color: textMuted, fontSize: 11 }}>({f.type})</span>
                    <span style={{ color: textMuted, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{f.value}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Beacon URL */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Beacon URL</label>
              <input
                type="text" value={beaconUrl} onChange={e => setBeaconUrl(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', background: inputBg, color: text, border: `1px solid ${inputBorder}`, borderRadius: 6, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 11, color: textMuted, marginTop: 4 }}>OOB callback URL. Each payload appends ?p=FIELD&v=VECTOR for identification.</div>
            </div>

            {/* SVG Beacon URL */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>SVG Beacon URL</label>
              <input
                type="text" value={svgBeaconUrl} onChange={e => setSvgBeaconUrl(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', background: inputBg, color: text, border: `1px solid ${inputBorder}`, borderRadius: 6, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 11, color: textMuted, marginTop: 4 }}>URL to SVG file with embedded script. Used for img-src injection pointing to hosted SVG.</div>
            </div>

            {/* Options */}
            <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={includeEventHandlers} onChange={e => setIncludeEventHandlers(e.target.checked)} style={{ width: 16, height: 16 }} />
                Include event-handler payloads (onerror, onload, onfocus, ...)
              </label>
              {meta.hasFileField && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={injectSvgUpload} onChange={e => setInjectSvgUpload(e.target.checked)} style={{ width: 16, height: 16 }} />
                  Test SVG beacon file upload (replaces file field with beacon-xss.svg)
                </label>
              )}
            </div>

            {/* Payload preview */}
            <div style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 6, background: darkMode ? '#111827' : '#f9fafb', border: `1px solid ${border}`, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>Payload Summary</div>
              <div>10 resource-load vectors (img, input[image], video, table[bg], audio, object, embed, iframe, link, script)</div>
              {includeEventHandlers && <div>+ 6 event-handler vectors (onerror, onload, onfocus, ontoggle, onstart)</div>}
              {injectSvgUpload && <div>+ SVG file upload with embedded beacon script</div>}
              <div style={{ marginTop: 6, color: textMuted }}>
                Total: ~{(selected.size * (includeEventHandlers ? 16 : 10)) + (injectSvgUpload ? 1 + selected.size : 0)} requests
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, paddingTop: 16, borderTop: `2px solid ${border}` }}>
              <button onClick={handleRun} disabled={running || (!selected.size && !injectSvgUpload)} style={{
                flex: 1, padding: '12px 24px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                opacity: (!selected.size && !injectSvgUpload) ? 0.5 : 1, boxShadow: '0 2px 8px rgba(239,68,68,0.3)'
              }}>
                {running ? <><FontAwesomeIcon icon={faSpinner} spin /> Injecting...</> : <><FontAwesomeIcon icon={faBullseye} /> Inject Beacons</>}
              </button>
              <button onClick={onClose} style={{
                padding: '12px 24px', background: darkMode ? '#374151' : '#f3f4f6', color: darkMode ? '#d1d5db' : '#374151',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </>
        ) : (
          /* Results view */
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <div style={{ padding: '8px 16px', borderRadius: 8, background: darkMode ? '#111827' : '#f0fdf4', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{results.summary?.totalSent || 0}</div>
                  <div style={{ fontSize: 11, color: textMuted }}>Sent</div>
                </div>
                <div style={{ padding: '8px 16px', borderRadius: 8, background: results.summary?.reflected ? (darkMode ? '#7f1d1d' : '#fef2f2') : (darkMode ? '#111827' : '#f0fdf4'), textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: results.summary?.reflected ? '#ef4444' : '#10b981' }}>{results.summary?.reflected || 0}</div>
                  <div style={{ fontSize: 11, color: textMuted }}>Reflected</div>
                </div>
                <div style={{ padding: '8px 16px', borderRadius: 8, background: results.findings?.length ? (darkMode ? '#7f1d1d' : '#fef2f2') : (darkMode ? '#111827' : '#f0fdf4'), textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: results.findings?.length ? '#f59e0b' : '#10b981' }}>{results.findings?.length || 0}</div>
                  <div style={{ fontSize: 11, color: textMuted }}>Findings</div>
                </div>
              </div>
              {results.note && <div style={{ fontSize: 13, color: textMuted, fontStyle: 'italic' }}>{results.note}</div>}
            </div>

            {/* Results table */}
            <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: darkMode ? '#111827' : '#f9fafb', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${border}` }}>Field</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${border}` }}>Vector</th>
                    <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: `1px solid ${border}` }}>Status</th>
                    <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: `1px solid ${border}` }}>Reflected</th>
                  </tr>
                </thead>
                <tbody>
                  {(results.tests || []).map((t, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${border}`, background: t.reflected ? (darkMode ? '#7f1d1d20' : '#fef2f220') : 'transparent' }}>
                      <td style={{ padding: '4px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{t.field}</td>
                      <td style={{ padding: '4px 10px', fontFamily: 'monospace', color: textMuted }}>{t.vector}</td>
                      <td style={{ padding: '4px 10px', textAlign: 'center' }}>{t.status}</td>
                      <td style={{ padding: '4px 10px', textAlign: 'center' }}>
                        {t.reflected ? (
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444' }} title="Beacon URL reflected in response" />
                        ) : t.accepted !== undefined ? (
                          t.accepted ? <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981' }} title="Upload accepted" /> : <span style={{ color: textMuted }}>rejected</span>
                        ) : (
                          <span style={{ color: textMuted }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 6, background: darkMode ? '#111827' : '#fffbeb', border: `1px solid ${darkMode ? '#92400e' : '#fde68a'}`, fontSize: 12, color: darkMode ? '#fcd34d' : '#92400e' }}>
              Monitor beacon logs at <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{results.beaconUrl}</span> for OOB callback confirmation. Reflected payloads may fire when a victim views the stored content.
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 16, paddingTop: 16, borderTop: `2px solid ${border}` }}>
              <button onClick={() => setResults(null)} style={{
                flex: 1, padding: '10px 20px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}>Run Again</button>
              <button onClick={onClose} style={{
                padding: '10px 20px', background: darkMode ? '#374151' : '#f3f4f6', color: darkMode ? '#d1d5db' : '#374151',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BeaconInjectionDialog;
